import _, {difference, isUndefined} from 'lodash';
import {createSelector} from 'reselect';

import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {isDefined} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  createConversationBasesLightStabilizer,
  createConversationLightStabilizer,
  createConversationsLightStabilizer,
} from '../../../../helpers/models/conversationStabilityHelpers';
import {AppParametricSelector} from '../../../../helpers/reselectHelpers';
import {
  ConversationBaseModel,
  ConversationModel,
  RConversation,
} from '../../../../models/api/conversationModel';
import {AsyncMoreStatusesEnum} from '../../../appTypes';
import {
  ConversationBasesSelector,
  OptionalConversationSelector,
} from '../../../data/conversations/conversationsSelectors';
import {getConversations, getMessages} from '../../../data/dataSelectors';
import {InboxesParametersSelector} from '../../inboxes/inboxesPanelSelectors';
import {InboxesParametersTypesEnum} from '../../inboxes/parameters/inboxesParameters';
import {
  areParametersCellsEqual,
  computeIsNormalSelection,
} from '../../inboxes/parameters/inboxesParametersHelpers';
import {CellsPanelId} from '../../panelStateBase';
import {makeGetOptionalCellsPanelPage} from '../cellsPanelSelectors';
import {
  CellsPanelPageState,
  CellsPanelPageTypesEnum,
  stateIsCellsPanelPageConversationStateWithSearchResults,
} from '../cellsPanelState';
import {
  emptyVisualSelection,
  findOnlyVisuallySelectedId,
  InboxesVisualConversationSelection,
  InboxesVisualMessageSelection,
  unknownVisualSelection,
} from '../helpers/cellsPanelSelectionHelpers';

/*
 * Visual selection selectors.
 */

/** Determines which conversations are selected for the given parameters and (optionally) its cells panel. */
export function makeGetConversationSelection<P>(
  getInboxesParameters: InboxesParametersSelector<P>,
  getCellsPanelId: AppParametricSelector<P, CellsPanelId | undefined> = () => undefined,
): AppParametricSelector<P, InboxesVisualConversationSelection> {
  // No need to memoize this.
  const getIsMessagesParameters: AppParametricSelector<P, boolean> = (state, props) => {
    const parameters = getInboxesParameters(state, props);
    return parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES;
  };

  // Higher-order selector, so we don't have to pull in the `getMessages` selector unless we're in Drafts/Sent.
  const getGetVisualConversationSelection = createSelector(getIsMessagesParameters, (isMessagesParameters) =>
    isMessagesParameters
      ? makeGetConversationSelectionForMessagesParameters(getInboxesParameters, getCellsPanelId)
      : makeGetConversationSelectionForConversationsParameters(getInboxesParameters, getCellsPanelId),
  );

  return (state, props) => getGetVisualConversationSelection(state, props)(state, props);
}

function makeGetConversationSelectionForConversationsParameters<P>(
  getInboxesParameters: InboxesParametersSelector<P>,
  getCellsPanelId: AppParametricSelector<P, CellsPanelId | undefined>,
): AppParametricSelector<P, InboxesVisualConversationSelection> {
  const getCellsPage = makeGetOptionalCellsPanelPage(getCellsPanelId);

  return createSelector([getCellsPage, getInboxesParameters], (cellsPage, parameters) => {
    if (parameters.type === InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
      return emptyVisualSelection;
    }

    if (computeIsNormalSelection(parameters.selection)) {
      return {
        isFullyKnown: true,
        knownIds: parameters.selection.selectedIds.filter(computeIsDefinedConversationId),
      };
    }

    // For an "all except" selection, try to gather as much as we can from the corresponding cells panel.
    if (
      cellsPage?.type === CellsPanelPageTypesEnum.CONVERSATIONS &&
      areParametersCellsEqual(parameters, cellsPage.parameters)
    ) {
      return {
        isFullyKnown: isCellsPanelPageFullyLoaded(cellsPage),
        knownIds: difference(
          cellsPage.conversationIds,
          parameters.selection.deselectedIds.filter(computeIsDefinedConversationId),
        ),
      };
    }

    return unknownVisualSelection;
  });
}

function makeGetConversationSelectionForMessagesParameters<P>(
  getInboxesParameters: InboxesParametersSelector<P>,
  getCellsPanelId: AppParametricSelector<P, CellsPanelId | undefined>,
): AppParametricSelector<P, InboxesVisualConversationSelection> {
  return createSelector(
    [getMessages, makeGetMessagesSelection(getInboxesParameters, getCellsPanelId)],
    (messages, messagesSelection) => {
      // Map the messages to their conversations.
      const conversationIds = _(messagesSelection.knownIds)
        .map((uid) => messages[uid]?.conversationId)
        .uniq()
        .value();
      const knownConversationIds = conversationIds.filter(isDefined);

      return {
        isFullyKnown:
          messagesSelection.isFullyKnown && conversationIds.length === knownConversationIds.length,
        knownIds: knownConversationIds.filter(computeIsDefinedConversationId),
      };
    },
  );
}

/** Determines which messages are selected for the given parameters and (optionally) its cells panel. */
export function makeGetMessagesSelection<P>(
  getInboxesParameters: InboxesParametersSelector<P>,
  getCellsPanelId: AppParametricSelector<P, CellsPanelId | undefined> = () => undefined,
): AppParametricSelector<P, InboxesVisualMessageSelection> {
  const getCellsPage = makeGetOptionalCellsPanelPage(getCellsPanelId);

  return createSelector([getCellsPage, getInboxesParameters], (cellsPage, parameters) => {
    if (parameters.type !== InboxesParametersTypesEnum.TEAMMATE_MESSAGES) {
      return emptyVisualSelection;
    }

    if (computeIsNormalSelection(parameters.selection)) {
      return {
        isFullyKnown: true,
        knownIds: parameters.selection.selectedIds.filter(computeIsDefinedMessageId),
      };
    }

    // For an "all except" selection, try to gather as much as we can from the corresponding cells panel.
    if (
      cellsPage?.type === CellsPanelPageTypesEnum.MESSAGES &&
      areParametersCellsEqual(cellsPage.parameters, parameters)
    ) {
      return {
        isFullyKnown: isCellsPanelPageFullyLoaded(cellsPage),
        knownIds: difference(
          cellsPage.messageUids,
          parameters.selection.deselectedIds.filter(computeIsDefinedMessageId),
        ),
      };
    }

    return unknownVisualSelection;
  });
}

/*
 * Resource selectors.
 */

/** If we know exactly one conversation is selected, tries to find that conversation in the state. */
export function makeGetOnlySelectedConversation<P>(
  getConversationSelection: AppParametricSelector<P, InboxesVisualConversationSelection>,
): OptionalConversationSelector<P> {
  const stabilizeConversation = createConversationLightStabilizer();

  return createSelector(
    [getConversations, getConversationSelection],
    (conversations, conversationSelection) => {
      const conversationId = findOnlyVisuallySelectedId(conversationSelection);
      if (isUndefined(conversationId)) {
        return undefined;
      }

      return stabilizeConversation(conversations[conversationId]);
    },
  );
}

export function pickMostRecentConversationBase(
  conversationBase: ConversationBaseModel | undefined,
  conversation: ConversationModel | undefined,
): ConversationBaseModel | undefined {
  if (!conversation) {
    return conversationBase;
  }

  if (conversationBase) {
    // If updatedAt values are the same, give priority to the full conversation
    // as some actions do not update updatedAt (e.g. tagging).
    return conversation.updatedAt >= conversationBase.updatedAt ? conversation : conversationBase;
  }

  return undefined;
}

/** Finds the potentially partial set of conversations that we know are selected. */
export function makeGetKnownSelectedConversations<P>(
  getConversationSelection: AppParametricSelector<P, InboxesVisualConversationSelection>,
  getCellsPanelId: AppParametricSelector<P, CellsPanelId | undefined>,
): ConversationBasesSelector<P> {
  const stabilizeConversations = createConversationsLightStabilizer();
  const stabilizeConversationBases = createConversationBasesLightStabilizer();
  const getCellsPage = makeGetOptionalCellsPanelPage(getCellsPanelId);

  return createSelector(
    [getConversations, getConversationSelection, getCellsPage],
    (conversations, conversationSelection, cellsPage) => {
      // When conversation search results are present, return those.
      if (cellsPage && stateIsCellsPanelPageConversationStateWithSearchResults(cellsPage)) {
        return stabilizeConversationBases(
          _(conversationSelection.knownIds)
            .map((id) =>
              pickMostRecentConversationBase(cellsPage.conversationSearchResults[id], conversations[id]),
            )
            .compact()
            .value(),
        );
      }

      return stabilizeConversations(
        _(conversationSelection.knownIds)
          .map((id) => conversations[id])
          .compact()
          .value(),
      );
    },
  );
}

export function makeGetSelectedLastManualMessages<P>(getCurrentConversations: ConversationBasesSelector<P>) {
  return createSelector([getCurrentConversations, getMessages], (currentConversations, messages) =>
    _(currentConversations)
      .map((conversation) => {
        if (!RConversation.is(conversation)) {
          return undefined;
        }

        // The lastMessage fallback covers the rare case in which an old conversation may not have a lastManualMessage in cache.
        const message = conversation.lastManualMessage ?? conversation.lastMessage;
        return message && messages[message.uid];
      })
      .compact()
      .value(),
  );
}

/** Finds the complete set of conversations that is selected, or an empty array if we don't know it. */
export function makeGetAllOrNoSelectedConversations<P>(
  getConversationSelection: AppParametricSelector<P, InboxesVisualConversationSelection>,
  getCellsPanelId: AppParametricSelector<P, CellsPanelId | undefined>,
): ConversationBasesSelector<P> {
  const stabilizeConversations = createConversationsLightStabilizer();
  const stabilizeConversationBases = createConversationBasesLightStabilizer();
  const getCellsPage = makeGetOptionalCellsPanelPage(getCellsPanelId);

  return createSelector(
    [getConversations, getConversationSelection, getCellsPage],
    (conversations, conversationSelection, cellsPage) => {
      const conversationIds = conversationSelection.isFullyKnown ? conversationSelection.knownIds : [];

      // When conversation search results are present, return those.
      if (cellsPage && stateIsCellsPanelPageConversationStateWithSearchResults(cellsPage)) {
        return stabilizeConversationBases(
          _(conversationIds)
            .map((id) =>
              pickMostRecentConversationBase(cellsPage.conversationSearchResults[id], conversations[id]),
            )
            .compact()
            .value(),
        );
      }

      return stabilizeConversations(
        _(conversationIds)
          .map((id) => conversations[id])
          .compact()
          .value(),
      );
    },
  );
}

/*
 * Helpers.
 */

function computeIsDefinedConversationId(id: number): boolean {
  return id !== 0;
}

function computeIsDefinedMessageId(id: string): boolean {
  return id !== '0';
}

function isCellsPanelPageFullyLoaded(page: CellsPanelPageState) {
  return (
    page.status === AsyncStatusesEnum.LOADED &&
    page.moreStatusTop === AsyncMoreStatusesEnum.ALL_LOADED &&
    page.moreStatusBottom === AsyncMoreStatusesEnum.ALL_LOADED
  );
}
